import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import * as ContractingFeedService from '~/services/ContractingFeedService';

import { CreateFeedWritePayload, FeedParentType, ListFeedEntriesQuery } from '~/types/contracting/feed';

// Posts reach Salesforce through a queue, usually within seconds, so refresh a little after posting.
const REFRESH_DELAYS_AFTER_POST_MS = [3000, 10000];

export const feedEntryQueryKeys = {
  all: ['contracting', 'feed-entries'] as const,
  parents: () => [...feedEntryQueryKeys.all, 'parent'] as const,
  parent: (parentType: FeedParentType, parentId: string) =>
    [...feedEntryQueryKeys.parents(), parentType, parentId] as const,
  list: (query: ListFeedEntriesQuery) =>
    [...feedEntryQueryKeys.parent(query.parent_type, query.parent_salesforce_id), query] as const,
};

export function useGetFeedEntries(query: ListFeedEntriesQuery) {
  const result = useInfiniteQuery({
    queryKey: feedEntryQueryKeys.list(query),
    queryFn: ({ pageParam, signal }) => ContractingFeedService.listFeedEntries(query, pageParam, { signal }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextPageToken ?? undefined,
    enabled: !!query.parent_salesforce_id,
  });

  return {
    ...result,
    entries: result.data?.pages.flatMap((page) => page.entries) ?? [],
  };
}

function useRefreshFeedAfterPost(parentType: FeedParentType, parentId: string) {
  const queryClient = useQueryClient();
  return () => {
    REFRESH_DELAYS_AFTER_POST_MS.forEach((delay) => {
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: feedEntryQueryKeys.parent(parentType, parentId) });
      }, delay);
    });
  };
}

export function useCreateFeedEntry(parentType: FeedParentType, parentId: string) {
  const refreshFeed = useRefreshFeedAfterPost(parentType, parentId);
  return useMutation({
    mutationFn: (payload: CreateFeedWritePayload) => ContractingFeedService.createFeedEntry(payload),
    onSuccess: refreshFeed,
  });
}

export function useCreateFeedComment(parentType: FeedParentType, parentId: string) {
  const refreshFeed = useRefreshFeedAfterPost(parentType, parentId);
  return useMutation({
    mutationFn: ({ feedEntryId, payload }: { feedEntryId: string; payload: CreateFeedWritePayload }) =>
      ContractingFeedService.createFeedComment(feedEntryId, payload),
    onSuccess: refreshFeed,
  });
}
